import type { FC, ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { SECTION } from 'src/constants/section';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { Survey } from 'src/types/survey';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import { createEmptyOrgTreeState } from 'src/components/common/OrgTree/helper';
import useOrgTreeState from 'src/components/common/OrgTree/hooks/useOrgTreeState';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';

import useFollowUpDetail from '../FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

import type { PaginatedFollowUpOwnersList } from './Sections/SelectOwner/usePotentialFollowUpOwners';
import { useSelectedOrgUnitsWithAncestors } from './hooks';

type FollowUpOwner = PaginatedFollowUpOwnersList['results'][number] | undefined;

export interface CreateActionItemContextProps {
  surveyId: number | undefined;
  setSurvey: (survey: Pick<Survey, 'id' | 'name' | 'year'> | undefined) => void;
  followUpActionModuleId?: number;
  selectedOrgUnits: ModuleOrPlanActionOrgUnit[];
  orgTreeState: OrgTreeState;
  followUpOwner: FollowUpOwner | undefined;
  setFollowUpOwner: (owner: FollowUpOwner | undefined) => void;
  isValid: boolean;
  isLoadingAncestors: boolean;
  confirmCreation: () => void;
}

const initialContextValue: CreateActionItemContextProps = {
  surveyId: undefined,
  setSurvey: () => {},
  selectedOrgUnits: [],
  orgTreeState: createEmptyOrgTreeState(),
  followUpOwner: undefined,
  setFollowUpOwner: () => {},
  isValid: false,
  isLoadingAncestors: false,
  confirmCreation: () => {},
};

export const CreateActionItemContext =
  createContext<CreateActionItemContextProps>(initialContextValue);

interface CreateActionItemProviderProps {
  preselectCurrentSurvey?: boolean;
  onConfirm: () => void;
  children: ReactNode;
}

export const CreateActionItemProvider: FC<CreateActionItemProviderProps> = ({
  preselectCurrentSurvey,
  onConfirm,
  children,
}) => {
  const { currentSurvey } = useUserContext();
  const { hasPermissionForModuleType, getModuleIdForModuleType } =
    useUserPermissions();
  const [survey, setSurvey] = useState<
    Pick<Survey, 'id' | 'name' | 'year'> | undefined
  >(
    (preselectCurrentSurvey &&
      hasPermissionForModuleType(SECTION.FOLLOW_UP_ACTION)) ||
      hasPermissionForModuleType(SECTION.FOLLOW_UP)
      ? currentSurvey
      : undefined,
  );
  const [followUpOwner, setFollowUpOwner] = useState<
    PaginatedFollowUpOwnersList['results'][number] | undefined
  >(undefined);
  const orgTreeState = useOrgTreeState();
  const { selectedOrgUnits } = orgTreeState;
  const selectedOrgUnitsWithAncestors = useSelectedOrgUnitsWithAncestors(
    selectedOrgUnits.map(({ id }) => id),
    survey?.id,
  );

  const followUpActionModuleId = useMemo(() => {
    if (!survey?.id) return undefined;

    return getModuleIdForModuleType(SECTION.FOLLOW_UP_ACTION, survey?.id);
  }, [survey, getModuleIdForModuleType]);

  const { setActionItemBeingCreated } = useFollowUpDetail();

  const confirmCreation = useCallback(() => {
    setActionItemBeingCreated({
      surveyId: survey?.id,
      surveyName: survey?.name,
      followUpOwner: followUpOwner?.id,
      followUpOwnerName: followUpOwner?.fullName,
      orgUnits: selectedOrgUnitsWithAncestors.data ?? [],
    });
    onConfirm();
  }, [
    onConfirm,
    setActionItemBeingCreated,
    survey,
    selectedOrgUnitsWithAncestors,
    followUpOwner,
  ]);

  const contextValue = useMemo(
    () => ({
      surveyId: survey?.id,
      setSurvey,
      selectedOrgUnits,
      orgTreeState,
      followUpOwner,
      setFollowUpOwner,
      isValid:
        survey?.id !== undefined &&
        selectedOrgUnits.length > 0 &&
        followUpOwner !== undefined,
      isLoadingAncestors: selectedOrgUnitsWithAncestors.isLoading,
      confirmCreation,
      followUpActionModuleId,
    }),
    [
      survey,
      selectedOrgUnits,
      orgTreeState,
      followUpOwner,
      confirmCreation,
      followUpActionModuleId,
    ],
  );

  return (
    <CreateActionItemContext.Provider value={contextValue}>
      {children}
    </CreateActionItemContext.Provider>
  );
};

export const useCreateActionItemContext = () =>
  useContext(CreateActionItemContext);
